import { useEffect, useState } from 'react';
import { signIn, signOut, currentEmail } from '../lib/authService';
import { getAal, needsStepUp, listTotpFactors, verifyTotp } from '../lib/mfa';
import TwoFactorPanel from './TwoFactorPanel';
import PasswordInput from './PasswordInput';

/**
 * Page de connexion (brique H) — `/login`.
 * Connexion par mot de passe + 2FA optionnelle (TOTP) : si le compte a un facteur
 * vérifié, un code à 6 chiffres est demandé après le mot de passe. Sans facteur,
 * la connexion est identique à avant (rétro-compatible).
 */
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [who, setWho] = useState<string | null>(null);
  // Étape 2FA (si le compte a un TOTP vérifié)
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  useEffect(() => {
    currentEmail().then(setWho);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await signIn(email, password);
    if (!res.ok) {
      setBusy(false);
      setError(res.error ?? 'Connexion impossible.');
      return;
    }
    // Le mot de passe a réussi → 2FA requise ?
    const aal = await getAal();
    if (needsStepUp(aal)) {
      const factors = await listTotpFactors();
      const verified = factors.find((f) => f.status === 'verified') ?? factors[0];
      setBusy(false);
      if (verified) {
        setMfaFactorId(verified.id);
        return;
      }
    }
    window.location.href = '/admin/super';
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId) return;
    setMfaBusy(true);
    setMfaError(null);
    const res = await verifyTotp(mfaFactorId, mfaCode);
    setMfaBusy(false);
    if (!res.ok) {
      setMfaError(res.error ?? 'Code invalide.');
      return;
    }
    window.location.href = '/admin/super';
  };

  const logout = async () => {
    await signOut();
    setWho(null);
    setMfaFactorId(null);
  };

  return (
    <div className="w-full max-w-sm">
      <h1 className="text-xl font-bold text-brand-primary mb-1">Connexion Lil &amp; Co</h1>
      <p className="text-sm opacity-70 mb-4">Espace d'administration réseau.</p>

      {who && (
        <div className="rounded-lg bg-slate-100 p-3 mb-4 text-sm flex items-center justify-between gap-2">
          <span>
            Connecté : <strong>{who}</strong>
          </span>
          <button onClick={logout} className="underline opacity-70">
            Se déconnecter
          </button>
        </div>
      )}

      {mfaFactorId ? (
        <form onSubmit={submitMfa} className="flex flex-col gap-3">
          <p className="text-sm">🔐 Saisis le code à 6 chiffres de ton application d'authentification.</p>
          <input
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            autoFocus
            className="w-full rounded border border-slate-300 px-3 py-2 tracking-widest"
          />
          <button
            type="submit"
            disabled={mfaBusy || mfaCode.length !== 6}
            className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
            style={{ background: 'var(--brand-primary)' }}
          >
            {mfaBusy ? 'Vérification…' : 'Valider le code'}
          </button>
          {mfaError && <p className="text-sm text-red-600">{mfaError}</p>}
        </form>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="text-sm font-semibold">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <label className="text-sm font-semibold">
            Mot de passe
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
            style={{ background: 'var(--brand-primary)' }}
          >
            {busy ? 'Connexion…' : 'Se connecter'}
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
      )}

      {who && !mfaFactorId && <TwoFactorPanel />}
    </div>
  );
}
